import { motion } from "motion/react";
import type { ReactNode, Ref } from "react";
import { useRef } from "react";

import type { EdgeSwipeBackTarget } from "@/lib/navigation/edgeSwipeBackLogic";
import { useEdgeSwipeBack } from "@/lib/navigation/useEdgeSwipeBack";

export type { EdgeSwipeBackTarget };

type EdgeSwipeBackLayerProps = {
  children: ReactNode;
  className?: string;
  /** Merged onto the gesture capture root (e.g. page shell ref for scroll anchoring). */
  rootRef?: Ref<HTMLDivElement>;
  enabled?: boolean;
  interactive?: boolean;
  backTarget?: EdgeSwipeBackTarget;
};

/**
 * Full-screen wrapper with iOS-style left-edge swipe-back.
 * Legal pages use `history`; Settings passes `{ pathname: "/today" }`.
 */
export function EdgeSwipeBackLayer({
  children,
  className,
  rootRef,
  enabled = true,
  interactive = true,
  backTarget = "history",
}: EdgeSwipeBackLayerProps) {
  const internalRef = useRef<HTMLDivElement>(null);

  const setRef = (node: HTMLDivElement | null) => {
    internalRef.current = node;
    if (typeof rootRef === "function") {
      rootRef(node);
    } else if (rootRef) {
      rootRef.current = node;
    }
  };

  const { x } = useEdgeSwipeBack(internalRef, { enabled, interactive, backTarget });

  return (
    <motion.div
      ref={setRef}
      className={className}
      style={{
        x: interactive ? x : 0,
        touchAction: "pan-y",
      }}
    >
      {children}
    </motion.div>
  );
}
